/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import {
  GateObservation,
  ObservationChangeLog,
  ObservationDraft,
  ObservationKey,
  ObservationMetadata,
  ObservationOutgateData,
} from '../models/client/observation'
import {
  buildApiResponseObservation,
  translateObservation,
  translateObservationChangeLog,
  translateObservationV2,
} from './tools'
import { GateFeedFilter, ObservationSort } from './type'
import { ApiMetaType, apiRequest, apiStreamReader } from './apiRequest'
import { ValidationError } from '../util/errors/ValidationError'
import { decodeUTF8 } from '../util/string'
import {
  API_OBSERVATION_STAT_KEYS,
  ApiObservation,
  ApiObservationAudit,
  ApiObservationResponseItem,
  ObservationStatKey,
} from '../models/api'
import { ApiError } from '../util/errors/ApiError'
import { ReportType } from '../models/client/constants'
import { ENABLE_MERGE_UPDATE_FOR_DELETE } from '../config/env'

const INCLUDED_OBSERVATION_FIELDS =
  '[vehicle.[type,carrier],chassis.[type],load.[type],gate.[site],vehicleOutwardObservation,chassisOutwardObservation,loadOutwardObservation]'

const sanitize = (value: string): string => value.trim()

const buildFilters = (params: {
  filters: GateFeedFilter[]
  inventoryOnly?: boolean
  report?: boolean
}): Record<string, any> => {
  const { filters, inventoryOnly, report } = params
  const filterParams = filters.reduce((draft, filter): Record<string, any> => {
    switch (filter.type) {
      case 'date':
        if (!filter.value.start || !filter.value.end) return draft
        draft.detectedAt = {
          $between: [filter.value.start, filter.value.end],
        }
        return draft
      case 'site': {
        draft['gate.siteId'] = filter.value.value
        return draft
      }
      case 'gate': {
        draft.gateId = filter.value.value
        return draft
      }
      case 'direction': {
        draft.direction = filter.value
        return draft
      }
      case 'property': {
        filter.value.forEach((propertyFilter) => {
          // licensePlate
          // if (propertyFilter.name === 'licensePlate') {
          //     draft.$or = [
          //         {
          //             'vehicle.licensePlate': {
          //                 $ilike: `${sanitize(propertyFilter.value)}%`,
          //             },
          //         },
          //         {
          //             'chassis.licensePlate': {
          //                 $ilike: `${sanitize(propertyFilter.value)}%`,
          //             },
          //         },
          //         {
          //             'load.licensePlate': {
          //                 $ilike: `${sanitize(propertyFilter.value)}%`,
          //             },
          //         },
          //     ]
          //     return
          // }
          draft[propertyFilter.name] = {
            $ilike: `${sanitize(propertyFilter.value)}%`,
          }

          if (report) {
            draft.loadOutwardObservationId = { $isnull: true }
            draft.vehicleOutwardObservationId = { $isnull: true }
            draft.chassisOutwardObservationId = { $isnull: true }
          }
        })

        return draft
      }
      // case 'isBare': {
      //     if (filter.value) {
      //         draft.direction = true
      //         draft.chassisId = {
      //             $notnull: true,
      //         }
      //         draft.chassisOutwardObservationId = {
      //             $isnull: true,
      //         }
      //     }
      //     return draft
      // }
      case 'id':
        draft.id = sanitize(filter.value)
        return draft
      case 'loadType':
        draft['load.typeId'] = filter.value
        return draft
      case 'vehicleType':
        draft['vehicle.typeId'] = filter.value
        return draft
      case 'chassisType':
        draft['chassis.typeId'] = filter.value
        return draft
      default:
        return draft
    }
  }, {} as Record<string, any>)

  return filterParams
}

const buildSort = (direction: 'ASC' | 'DESC', column: ObservationKey): string => {
  const columnsToField = {
    assetTimestamp: 'detectedAt',
    assetDirection: 'direction',
    loadType: 'load.type.name',
    loadNumber: 'load.number',
    hasSeal: 'hasSeal',
    sealNumber: 'sealNumber',
    loadLicensePlate: 'load.licensePlate',
    loadDamage: 'loadDamage',
    loadDwellTimeHr: 'load_dwell_time',
    chassisNumber: 'chassis.number',
    chassisLicensePlate: 'chassis.licensePlate',
    chassisDamage: 'chassisDamage',
    isBare: 'chassisIsBare',
    chassisDwellTimeHr: 'chassis_dwell_time',
    vehicleType: 'vehicle.type.name',
    vehicleCarrierName: 'vehicle.carrier.name',
    vehicleNumber: 'vehicle.number',
    vehicleDotNumber: 'vehicle.dotNumber',
    vehicleLicensePlate: 'vehicle.licensePlate',
    vehicleDamage: 'vehicleDamage',
    vehicleDwellTimeHr: 'vehicle_dwell_time',
    vehicleWeightClass: 'vehicle.weightClass',
    vehicleFuelType: 'vehicle.fuelType',
  }
  if (!columnsToField[column]) {
    throw new ApiError({
      type: 'BadRequest',
      cause: new Error(`Invalid sort key: ${JSON.stringify(column)}`),
    })
  }
  return `${columnsToField[column]} ${direction}`
}

const getSiteGateFeed = async (params: {
  filters: GateFeedFilter[]
  token: string
  page?: number
  size?: number
  reportType?: ReportType
  includeVehicles?: boolean
  sort?: ObservationSort | null
}): Promise<{
  data: GateObservation[]
  meta: ApiMetaType
}> => {
  const { filters, token, page = 0, size = 10, reportType, sort, includeVehicles } = params
  const inventoryOnly = reportType === 'yardreport'
  // @ts-ignore
  const sortColumn =
    sort?.column === 'TIME' || sort?.column === 'DATE' ? 'assetTimestamp' : sort?.column
  const sortBy = buildSort(sort?.direction || 'DESC', sortColumn || 'assetTimestamp')
  const report = !!inventoryOnly

  let filter
  const isBare = filters.filter((f) => f.id === 'isBare').length > 0

  if (report) {
    filter = {
      ...buildFilters({ filters, inventoryOnly }),
      // loadId: {
      //     $notnull: '',
      // },
      // 'load.type.name': {
      //     $in: ['Container', 'Container Reefer'],
      // },
      // loadOutwardObservationId: {
      //     $isnull: '',
      // },
    }
  } else {
    filter = {
      ...buildFilters({ filters, inventoryOnly }),
    }
  }
  // exclude goats
  // filter['vehicle.type.name'] = ['!=', 'GOAT']
  const { data: observations, meta } = await apiRequest<ApiObservationResponseItem[]>({
    path: inventoryOnly ? '/observations/yard-report' : '/observations/gate-feed',
    token,
    method: 'POST',
    include: inventoryOnly ? [INCLUDED_OBSERVATION_FIELDS] : [],
    data: {
      page: {
        number: page,
        size,
      },
      filter,
      sort: sortBy,
      report,
      isBare,
      includeVehicles,
    },
  })

  const nextObservations: GateObservation[] = []

  observations.forEach((observation) => {
    try {
      const translatedObservation = inventoryOnly
        ? translateObservation(observation)
        : translateObservationV2(observation)

      nextObservations.push(translatedObservation)
    } catch (e) {
      if (e instanceof ValidationError) {
        return
      }
      throw e
    }
  })

  return {
    data: nextObservations,
    meta,
  }
}

const getGateObservation = async (params: {
  token: string
  observationId: string
}): Promise<GateObservation> => {
  const { data } = await apiRequest<ApiObservationResponseItem>({
    token: params.token,
    path: `/observations/${params.observationId}`,
    include: [INCLUDED_OBSERVATION_FIELDS],
    method: 'GET',
  })
  return translateObservation(data)
}

const getChangeLog = async (params: {
  token: string
  companyId: string
  page?: number
  size?: number
}): Promise<{ data: ObservationChangeLog[]; meta: ApiMetaType }> => {
  const { token, page = 0, size = 10 } = params
  const { data: res, meta } = await apiRequest<Array<ApiObservationAudit>>({
    method: 'GET',
    token,
    path: '/observation_audits',
    parameters: {
      page: {
        number: page,
        size,
      },
      sort: 'createdAt DESC',
    },
  })
  const data: Array<ObservationChangeLog> = []

  res.forEach((responseItem) => {
    try {
      data.push(translateObservationChangeLog(responseItem))
    } catch (e) {
      if (!(e instanceof ValidationError)) {
        throw e
      }
    }
  })
  return { data, meta }
}

const addObservation = async ({
  draft,
  token,
}: {
  draft: ObservationDraft
  token: string
}): Promise<GateObservation> => {
  const { data } = await apiRequest<ApiObservation, ApiObservationResponseItem>({
    path: `/observations`,
    method: 'POST',
    token,
    data: buildApiResponseObservation({ draft, isNew: true }),
    include: [INCLUDED_OBSERVATION_FIELDS],
  })
  return translateObservation(data)
}

const addObservationV2 = async ({
  token,
  draft,
  meta,
  report,
}: {
  token: string
  draft: ObservationDraft
  meta?: ObservationMetadata
  report?: boolean
}): Promise<void> => {
  let payload: any = {}

  const CUSTOMER_EXCEPTION = '3'
  const observation = buildApiResponseObservation({ draft, meta })

  if (report) {
    // (Inventory) Report: add observation via observations/merge (no-match)
    payload = {
      data: {
        observations: [
          {
            ...observation,
            tags: ['manual'],
            statusCode: CUSTOMER_EXCEPTION,
            detectedAt: observation.detectedAt ?? new Date().toISOString(), // SPEC: fill in wall time unless `detectedAt` is specified - since required (NOTE: also done in UI editor default values)
            direction: true, // ingate

            // SPEC: fill in Vehicle type to be `Tractor` - since required (TODO)
          },
        ],
      },
      options: {
        noMatch: true, // create
      },
    }
  } else {
    // Gate Feed: add observation via observations/merge (no-match)
    payload = {
      data: {
        observations: [
          {
            ...observation,
            tags: ['manual'],
            statusCode: CUSTOMER_EXCEPTION,
            detectedAt: observation.detectedAt ?? new Date().toISOString(), // SPEC: fill in wall time unless `detectedAt` is specified - since required (NOTE: also done in UI editor default values)

            // SPEC: fill in Vehicle type to be `Tractor` - since required (TODO)
          },
        ],
      },
      options: {
        noMatch: true, // create
      },
    }
  }
  payload.data = payload.data.observations
  console.warn('addObservationV2', { report, payload })

  const result = await apiRequest<any, any>({
    method: 'POST',
    path: `/observations/merge`,
    token,
    data: payload.data,
    parameters: payload.options,
    include: [INCLUDED_OBSERVATION_FIELDS],
    disableCache: true,
  })
  const data = result?.data

  // return translateObservation(data)
  return null
}

const editObservation = async ({
  token,
  observationId,
  draft,
  meta,
}: {
  token: string
  observationId: string
  draft: ObservationDraft
  meta: ObservationMetadata
}): Promise<GateObservation> => {
  const { data } = await apiRequest<ApiObservation, ApiObservationResponseItem>({
    path: `/observations/${observationId}`,
    method: 'PATCH',
    token,
    data: buildApiResponseObservation({ draft, meta }),
    include: [INCLUDED_OBSERVATION_FIELDS],
    disableCache: true,
    meta: {
      loadCustomerName: draft.loadCustomerName,
    },
  })

  return translateObservation(data)
}

const editObservationV2 = async ({
  token,
  observationId,
  draft,
  meta,
  report,
}: {
  token: string
  observationId: string
  draft: ObservationDraft
  meta?: ObservationMetadata
  report?: boolean
}): Promise<void> => {
  let payload: any = {}

  const CUSTOMER_EXCEPTION = '3'
  const observation = buildApiResponseObservation({ draft, meta })

  if (report) {
    // (Inventory) Report: update observation via observations/merge (match)
    payload = {
      data: {
        observations: [
          {
            ...observation,
            id: observationId,
            statusCode: CUSTOMER_EXCEPTION,
          },
        ],
      },
      options: {
        noMatch: false, // merge/update
      },
    }
    // NOTE: currently cannot use `/merge` and corresponding payload for all requests, so for now mapping over
    payload = {
      data: payload?.data?.observations?.[0] || null,
    }
  } else {
    // Gate Feed: update observation via observations/merge (match)
    payload = {
      data: {
        observations: [
          {
            ...observation,
            id: observationId,
            statusCode: CUSTOMER_EXCEPTION,
          },
        ],
      },
      options: {
        noMatch: false, // merge/update
      },
    }
    // NOTE: currently cannot use `/merge` and corresponding payload for all requests, so for now mapping over
    payload = {
      data: payload?.data?.observations?.[0] || null,
    }
  }

  console.warn('editObservationV2', { report, payload })

  const result = await apiRequest<ApiObservation, ApiObservationResponseItem>({
    method: 'PUT',
    path: '/observations/merge-update',
    token,
    data: payload.data,
    parameters: payload.options,
    include: [INCLUDED_OBSERVATION_FIELDS],
    disableCache: true,
  })
  // return translateObservation(data)
  return null
}

const deleteObservation = async ({
  token,
  observationId,
}: {
  token: string
  observationId: string
}): Promise<void> => {
  await apiRequest<void>({
    path: `/observations/${observationId}`,
    method: 'DELETE',
    token,
  })
}

const deleteObservationV2 = async ({
  token,
  observationId,
  draft,
  meta,
  report,
}: {
  token: string
  observationId: string
  draft?: ObservationDraft
  meta?: ObservationMetadata
  report?: boolean
}): Promise<void> => {
  let payload: any = {}

  const CUSTOMER_EXCEPTION = '3'
  const observation = buildApiResponseObservation({ draft, meta })

  if (report) {
    // (Inventory) Report: add outward observation via observations/merge (no-match)
    payload = {
      data: {
        observations: [
          {
            ...observation,
            id: observationId,
            statusCode: CUSTOMER_EXCEPTION,
            direction: false, // SPEC: create outgate observation
          },
        ],
      },
      options: {
        noMatch: true, // create
      },
    }
    if (ENABLE_MERGE_UPDATE_FOR_DELETE) {
      // NOTE: currently cannot use `/merge` and corresponding payload for all requests, so for now mapping over
      payload = {
        data: payload?.data?.observations?.[0] || null,
      }
    }
  } else {
    // Gate Feed: update observation via observations/merge (match)
    payload = {
      data: {
        observations: [
          {
            ...observation,
            id: observationId,
            statusCode: CUSTOMER_EXCEPTION,
            archivedAt: new Date().toISOString(), // SPEC: delete = archive
          },
        ],
      },
      options: {
        noMatch: false, // update
      },
    }
    if (ENABLE_MERGE_UPDATE_FOR_DELETE) {
      // NOTE: currently cannot use `/merge` and corresponding payload for all requests, so for now mapping over
      payload = {
        data: payload?.data?.observations?.[0] || null,
      }
    }
  }

  // NOTE: remove `*_outward_observation_id` references pointing to observations that are `archivedAt` (external implementation)

  console.warn('deleteObservationV2', { report, payload })

  const result = await apiRequest<any, any>({
    method: ENABLE_MERGE_UPDATE_FOR_DELETE ? 'PUT' : 'POST',
    path: ENABLE_MERGE_UPDATE_FOR_DELETE ? '/observations/merge-update' : `/observations/merge`,
    token,
    data: payload.data,
    parameters: payload.options,
    include: [INCLUDED_OBSERVATION_FIELDS],
    disableCache: true,
  })
  const data = result?.data

  // return translateObservation(data)
  return null
}
const outgateObservationV2 = async ({
  token,
  observationId,
  observation,
  outgateDateTime,
}: {
  token: string
  observationId: string
  observation?: ObservationOutgateData
  outgateDateTime: string
}): Promise<void> => {
  let payload: any = {}

  payload = {
    observation: {
      id: observationId,
      detectedAt: observation.assetTimestamp,
      gateId: observation.gateId,
      loadId: observation.loadId || null,
      chassisId: observation.chassisId || null,
    },
    outgateSource: 'YMS',
    outgateTimestamp: outgateDateTime,
  }

  console.warn('outgating observation', payload)

  const result = await apiRequest<any, any>({
    method: 'POST',
    path: `/observations/outgate`,
    token,
    data: { ...payload },
  })

  return result?.data
}

export type ObservationStats = {
  key: ObservationStatKey
  label: string
  count: number
}

const getObservationReport = async ({
  token,
  siteId,
}: {
  token: string
  siteId?: string
}): Promise<ObservationStats[]> => {
  const siteFilters = siteId
    ? {
        'gate.siteId': siteId,
      }
    : {}
  const { meta } = await apiRequest<
    unknown,
    unknown,
    {
      stats: Array<{
        name: string
        count: number
      }>
    }
  >({
    path: `/observations`,
    token,
    method: 'GET',
    parameters: {
      report: true,
      stats: true,
      // include: 'chassisOutwardObservation,vehicleOutwardObservation,loadOutwardObservation',
      filter: {
        ...siteFilters,
      },
    },
  })

  return API_OBSERVATION_STAT_KEYS.map((key) => {
    const getCount = (statKey: string): number =>
      meta.stats.find(({ name: nextKey }) => nextKey === statKey)?.count || 0

    switch (key) {
      case 'Chassis':
        return {
          key,
          label: 'Chassis',
          count: getCount(key),
        }
      case 'Load Container':
        return {
          key,
          label: 'Container',
          count: getCount(key),
        }
      case 'Load Container Reefer':
        return {
          key,
          label: 'Container Reefer',
          count: getCount(key),
        }
      case 'Load Dry Van':
        return {
          key,
          label: 'Dry Van',
          count: getCount(key),
        }
      case 'Load Dry Van Reefer':
        return {
          key,
          label: 'Dry Van Reefer',
          count: getCount(key),
        }
      // case 'Bare Chassis':
      //     return {
      //         key,
      //         label: 'Bare Chassis',
      //         count: getCount(key)
      //     }
      case 'Vehicle Tractor':
        return {
          key,
          label: 'Tractor',
          count: getCount(key),
        }
      // default:
      //     // NOTE: should only occur on "Bare Chassis"
      //     return {
      //         key,
      //         label: key,
      //         count: getCount(key)
      //     }
      default:
        return null
    }
  }).filter(Boolean)
}

export type CancelDownloadFn = () => void

const readObservationCSV = async ({
  token,
  filters = [],
  fields,
  report,
  includeVehicles,
  inventoryOnly,
  timezone,
  exportDate,
  exportFormat,
  onComplete,
  onUpdate,
  onError,
}: {
  token: string
  filters?: GateFeedFilter[]
  report?: boolean
  fields?: object
  inventoryOnly?: boolean
  timezone?: string
  exportDate?: string
  exportFormat?: string
  includeVehicles?: boolean
  onComplete?: (params: { data: string; fileName: string; contentLength: number }) => void
  onUpdate?: (params: { totalLength: number; receivedLength: number }) => void
  onError?: (e: Error) => void
}): Promise<CancelDownloadFn> => {
  try {
    const exportModalFields = {
      observations: [
        'sealNumber',
        'vehicleRemarks',
        'chassisRemarks',
        'loadRemarks',
        'vehicleDamage',
        'loadDamage',
        'chassisDamage',
      ],
    }

    let include
    let fields = exportFormat === 'assetExport' ? exportModalFields : {}
    let filter
    let sort

    if (report) {
      // include = include || '(load.(type), chassis)'
      fields =
        fields ||
        {
          // sites: ['name'],
          // loads: ['number'],
          // chassis: ['number'],
          // types: ['name'],
        }
      filter = filter || {
        ...buildFilters({
          filters,
          inventoryOnly,
          report,
        }),
        // loadId: {
        //     $notnull: '',
        // },
        // 'load.type.name': {
        //     $in: ['Container', 'Container Reefer'],
        // },
        // loadOutwardObservationId: {
        //     $isnull: '',
        // },
      }
      sort = sort || '+detectedAt'
    } else {
      include = include || '(load.(type), chassis.(type), vehicle.(type, carrier))'
      fields = fields || {
        observations: ['direction', 'hasSeal', 'detectedAt'],
        sites: ['name'],
        loads: ['number', 'licensePlate', 'state'],
        chassis: ['number', 'licensePlate', 'state'],
        vehicles: ['number', 'licensePlate', 'dotNumber', 'state'],
        types: ['name'],
        carriers: ['name'],
      }
      filter = filter || {
        ...buildFilters({
          filters,
          inventoryOnly,
          report,
        }),
      }
      sort = sort || '+detectedAt'
    }

    const isBare = filters.filter((f) => f.id === 'isBare').length > 0

    const {
      reader,
      meta: { contentLength, fileName },
    } = await apiStreamReader({
      path: report ? '/observations/yard-report' : '/observations/gate-feed',
      token,
      data: {
        include,
        fields,
        filter,
        sort,
        report,
        isBare,
        export: true,
        exportDate,
        exportFormat,
        includeVehicles,
      },
      inventoryOnly,
      timezone,
    })

    const execute = async (): Promise<void> => {
      const chunks: Array<Uint8Array> = []
      let receivedLength = 0
      onUpdate?.({ receivedLength: 0, totalLength: contentLength })
      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read()

        if (done) break
        if (value) chunks.push(value)
        receivedLength += value?.length || 0
        onUpdate?.({ receivedLength, totalLength: contentLength })
      }
      let csvData = ''
      chunks.forEach((chunk) => {
        csvData += decodeUTF8(chunk)
      })
      onComplete?.({ data: csvData, fileName, contentLength })
    }
    execute()
    return (): void => {
      reader.cancel()
    }
  } catch (e) {
    if (e instanceof Error) {
      onError?.(e)
    }

    return () => {}
  }
}

export const observationClient = {
  getSiteGateFeed,
  getChangeLog,
  addObservation,
  addObservationV2,
  editObservation,
  editObservationV2,
  deleteObservation,
  deleteObservationV2,
  outgateObservationV2,
  getObservationReport,
  getGateObservation,
  readObservationCSV,
}
